import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { View, StyleSheet, Text, Platform, TouchableOpacity, ActivityIndicator, useWindowDimensions } from 'react-native';
import { Toast, ToastType } from '../common/Toast';
import { ConfirmationModal } from '../common/ConfirmationModal';
import Feather from '@expo/vector-icons/Feather';
import { useRouter } from 'expo-router';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import {
  selectCurrentWeekMeals,
  selectMealsForWeek,
  selectMealsForDateRange,
  setWeeklyPlan,
  setCurrentWeek,
  clearWeek,
  replaceMeal,
  removeFromWeek,
  addToWeek,
  addFreeTextMeal,
  moveMeal,
  updateMealRecipe,
  updateMealServings,
  markMealAsCooked,
  unmarkMealAsCooked,
  PlannedMeal,
  MealType,
  getTodayStart,
  MAX_MEALS_PER_SLOT,
} from '../../redux/mealPlanSlice';
import { isRecipeMeal } from '../../redux/mealEntry';
import {
  isFishRecipe,
  isVegetarianRecipe,
  isSlowCookerRecipe,
  DinnerMVPConfig,
  type GeneratedDinner,
} from '../../services/weekGeneratorService';
import { horizonRange, generateHorizonDinners, groupMealsByWeek } from '../../services/horizonWeek';
import { historyFromMeals, SAME_MEAL_LOOKBACK_DAYS } from '../../services/mealIdentity';
import { HORIZON_DAYS } from '../../services/horizonDays';
import personalRecipeIndex, { PersonalRecipeIndexEntry } from '../../services/personalRecipeIndex';
import personalRecipeDetails from '../../services/personalRecipeDetails';
import unifiedRecipeService from '../../services/unifiedRecipeService';
import { loadPlannerLibrary, mergePlannerLibrary } from '../../services/plannerLibrary';
import { setRecipeIndex } from '../../redux/recipeIndexSlice';
import { isFirebaseEnabled } from '../../services/firebaseConfig';
import { Paprika, PaprikaSpacing, PaprikaRadius, PaprikaTypography } from '../../styles/theme';
import { WeekDesktopView } from './WeekDesktopView';
import { WeeklyDesktop } from './v2/desktop/WeeklyDesktop';
import { WeeklyMobile } from './v2/mobile/WeeklyMobile';
import { MealConfigDesktop } from '../meal-config/desktop/MealConfigDesktop';
import { MealConfigMobile } from '../meal-config/mobile/MealConfigMobile';
import { selectConfigPhase } from '../../redux/mealPlanUISlice';
import { DragDropProvider } from '../../context/DragDropContext';
import { isDesktopWeb } from '../../utils/platformDetection';
import { usePlannerLayout } from '../../hooks/usePlannerLayout';
import GenerateWizard, { GenerateConfig, MealSlot } from './GenerateWizard';
import GenerationSummaryBanner from './GenerationSummaryBanner';
import { buildGenerationSummary, GenerationSummary } from '../../services/generationSummaryService';
import IngredientReviewSheet from './IngredientReviewSheet';
import weekGeneratorService, { WeekSlot, DietaryConfig } from '../../services/weekGeneratorService';
import recipeService from '../../services/recipeService';
import RecipeSuggestionSheet from './RecipeSuggestionSheet';
import SwapSuggestionSheet from './SwapSuggestionSheet';
import AddCustomMealModal from './AddCustomMealModal';
import { selectSelectedDeals, selectAllDeals, fetchLufaDeals } from '../../redux/dealsSlice';
import { selectActiveInventory } from '../../redux/inventorySlice';
import {
  selectMealSchedule, selectDefaultFamilySize, selectAllergens, selectCustomAllergens,
  selectTasteAxes, RejectionReason,
} from '../../redux/userPreferencesSlice';
import { buildAllergenTerms } from '../../services/allergenMatcher';
import { useRecipeEnrichment } from '../../hooks/useRecipeEnrichment';
import { useMealPlanTranslation } from '../../hooks/useMealPlanTranslation';
import { useLufaDealsEnabled } from '../../hooks/useLufaDealsEnabled';
import { translateMealPlanRecipes, type TranslatedMealUpdate } from '../../services/mealPlanTranslation';
import { selectCurrentLanguage } from '../../redux/languageSlice';
import { useReadableRecipeIndex } from '../../hooks/useReadableRecipeIndex';
import { Recipe } from '../../types/recipe';
import { parseIngredientText, parseAmount } from '../../utils/ingredientParser';
import { useTranslation } from 'react-i18next';
import { formatWeekday } from '../../utils/dateLocale';
import SkipFeedbackModal, { FeedbackContext } from '../mealPlanning/SkipFeedbackModal';
import { addFeedbackEvent, FeedbackEvent, selectTasteProfile } from '../../redux/feedbackSlice';
import {
  createSwapEvent,
  createServingsChangeEvent,
  createSilentRegenEvent,
  createReplacedWithFreetextEvent,
  createMealCompletedEvent,
} from '../../services/feedbackEventHelpers';
import { DEV_API_ORIGIN } from '../../config/apiBase';
import { todayIso, addDays, weekStartOf } from '../../utils/localDate';

// Helper to get the correct API URL in dev vs production
/** Stable empty array so a cook with no declared preference does not re-render. */
const EMPTY_PREFS: string[] = [];

const getApiUrl = (path: string): string => {
  // @ts-ignore - __DEV__ is a React Native global
  if (__DEV__ && Platform.OS === 'web') {
    return `${DEV_API_ORIGIN}${path}`;
  }
  return path;
};

interface TheWeeklyScreenProps {
  swapRecipeId?: string;
  swapDay?: number;
  swapMealType?: 'breakfast' | 'lunch' | 'dinner';
}

export const TheWeeklyScreen: React.FC<TheWeeklyScreenProps> = ({ swapRecipeId, swapDay, swapMealType } = {}) => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const { t, i18n } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  // 'grid' only for a touch tablet in landscape; desktop returns 'desktop'
  // and falls through to the width branches below unchanged (#646).
  const plannerLayout = usePlannerLayout();

  const currentWeekStart = useSelector((state: RootState) => state.mealPlan?.currentWeekStart ?? getTodayStart());
  const currentWeekMeals = useSelector(selectCurrentWeekMeals);
  // Whether an empty week means "no plan" or "not loaded yet".
  const allMetadata = useSelector((state: RootState) => state.recipeMetadata?.metadata ?? {});
  const selectedDeals = useSelector(selectSelectedDeals);
  const allDeals = useSelector(selectAllDeals);
  const userEmail = useSelector((state: RootState) => state.auth?.user?.email);
  const uid = useSelector((state: RootState) => state.auth?.user?.uid ?? null);
  const recipeIndexEntries = useReadableRecipeIndex();
  const activeInventory = useSelector(selectActiveInventory);
  const mealSchedule = useSelector(selectMealSchedule);
  const defaultFamilySize = useSelector(selectDefaultFamilySize);
  const tasteProfile = useSelector(selectTasteProfile);
  const tasteAxes = useSelector(selectTasteAxes);
  const allergens = useSelector(selectAllergens);
  const customAllergens = useSelector(selectCustomAllergens);
  const dislikedIngredients = useSelector((state: RootState) => state.userPreferences?.dislikedIngredients ?? EMPTY_PREFS);
  const dietaryRestrictions = useSelector((state: RootState) => state.userPreferences?.dietaryRestrictions ?? EMPTY_PREFS);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const configPhase = useSelector(selectConfigPhase);

  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [reviewMeal, setReviewMeal] = useState<PlannedMeal | null>(null);
  const [isReviewSheetVisible, setIsReviewSheetVisible] = useState(false);
  const [bundledRecipes, setBundledRecipes] = useState<PersonalRecipeIndexEntry[]>([]);
  // The planner's library (`plannerLibrary.ts`): the catalogue and the cook's
  // own recipes once the index is in, the bundled recipes until then.
  const allRecipesLoaded = useMemo(
    () => mergePlannerLibrary(recipeIndexEntries, bundledRecipes),
    [recipeIndexEntries, bundledRecipes],
  );

  // Suggestion sheet state
  const [showSuggestionSheet, setShowSuggestionSheet] = useState(false);
  const [suggestionSlot, setSuggestionSlot] = useState<WeekSlot | null>(null);

  // Custom meal modal state
  const [showCustomMealModal, setShowCustomMealModal] = useState(false);
  const [customMealSlot, setCustomMealSlot] = useState<WeekSlot | null>(null);
  const [dietaryConfig, setDietaryConfig] = useState<DietaryConfig>({
    includeVegetarian: true,
    includeFish: true,
  });

  // Swap suggestion sheet state
  const [swapSheetVisible, setSwapSheetVisible] = useState(false);
  const [swapSheetMeal, setSwapSheetMeal] = useState<PlannedMeal | null>(null);

  // Feedback modal state
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [pendingFeedbackMeal, setPendingFeedbackMeal] = useState<PlannedMeal | null>(null);
  const [feedbackContext, setFeedbackContext] = useState<FeedbackContext>('remove');

  // Generation summary banner
  const [generationSummary, setGenerationSummary] = useState<GenerationSummary | null>(null);



  // Toast state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<ToastType>('success');

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }, []);

  // Background enrichment: fetch missing cook times or source ratings for planned meals
  const recipesForEnrichment = useMemo(() => {
    return currentWeekMeals
      .filter(m => m.recipeId !== 'free-text' && m.recipe && (
        (!m.recipe.readyInMinutes || m.recipe.readyInMinutes === 0) ||
        !m.recipe.sourceRating
      ))
      // The meal's recipeId is the document id; its recipe copy may carry only
      // a hashed `id`, which the enrich endpoint cannot look up (#778).
      .map(m => ({ ...m.recipe, _firestoreId: m.recipe!._firestoreId ?? m.recipeId }) as Recipe);
  }, [currentWeekMeals]);

  const handleRecipeEnriched = useCallback((recipeId: string, data: Partial<Recipe>) => {
    const meal = currentWeekMeals.find(m => m.recipeId === recipeId);
    if (meal?.recipe && (data.readyInMinutes || data.sourceRating != null)) {
      dispatch(updateMealRecipe({
        recipeId,
        recipe: { ...meal.recipe, ...data } as Recipe,
      }));
    }
  }, [currentWeekMeals, dispatch]);

  useRecipeEnrichment(recipesForEnrichment, handleRecipeEnriched);

  // Auto-translate meal plan recipes when language changes
  useMealPlanTranslation();

  const lufaEnabled = useLufaDealsEnabled();

  // Rehydrate recipe ingredients from full recipe details (async, non-blocking)
  // Replaces stub extendedIngredients (amount:1, no original) with real data from recipe JSON files
  const rehydrateRecipeIngredients = useCallback(async (recipeIds: string[]) => {
    try {
      const uniqueIds = [...new Set(recipeIds)];
      console.log(`[Rehydrate] Loading full details for ${uniqueIds.length} recipes...`);
      const recipes = await personalRecipeDetails.getMultipleRecipes(uniqueIds);

      let updatedCount = 0;
      recipes.forEach((recipe, index) => {
        if (!recipe || !recipe.extendedIngredients?.length) return;

        const recipeId = uniqueIds[index];
        dispatch(updateMealRecipe({
          recipeId,
          recipe: {
            ...recipe,
            id: recipeId,
          },
        }));
        updatedCount++;
      });

      console.log(`[Rehydrate] Updated ${updatedCount}/${uniqueIds.length} recipes with full ingredient data`);
    } catch (error) {
      console.warn('[Rehydrate] Failed to rehydrate recipe ingredients:', error);
    }
  }, [dispatch]);

  // Translate meal plan recipes to match current app language (async, non-blocking)
  const translateGeneratedMeals = useCallback(async (meals: PlannedMeal[]) => {
    const apply = (update: TranslatedMealUpdate) => {
      const meal = meals.find(m => m.recipeId === update.recipeId);
      if (!meal?.recipe) return;
      // Prefer the full translation (ingredients + instructions) so the
      // grocery list, which reads extendedIngredients, is in one language.
      dispatch(updateMealRecipe({
        recipeId: update.recipeId,
        recipe: update.translatedRecipe ?? {
          ...meal.recipe,
          title: update.translatedTitle,
          isTranslated: true,
          translatedFrom: meal.recipe.originalLanguage === 'fr' ? 'fr' : 'en',
        },
      }));
    };

    try {
      // The third argument applies each background translation as it lands,
      // which is what turns a title-only fallback into real translated
      // ingredients without the user reloading.
      const updates = await translateMealPlanRecipes(meals, currentLanguage, apply);
      updates.forEach(apply);
    } catch (error) {
      console.warn('Failed to translate meal plan recipes:', error);
    }
  }, [currentLanguage, dispatch]);

  // Compute empty slots for the week
  const emptySlots = useMemo((): WeekSlot[] => {
    // Generate all slots for the week
    const allSlots = weekGeneratorService.generateWeekSlots(currentWeekStart);

    // Create a set of filled slot keys
    const filledSlotKeys = new Set(
      currentWeekMeals.map(meal => `${meal.scheduledFor}-${meal.mealType}`)
    );

    // Filter to only empty slots
    const empty = allSlots.filter(slot => !filledSlotKeys.has(`${slot.date}-${slot.mealType}`));

    return empty;
  }, [currentWeekStart, currentWeekMeals]);

  // Check if week has any meals
  const hasAnyMeals = currentWeekMeals.length > 0;

  // The bundled recipes, the library's fallback until the catalogue arrives.
  useEffect(() => {
    personalRecipeIndex.getAllRecipes(currentLanguage).then(setBundledRecipes);
  }, [currentLanguage]);

  // Ensure the shared Redux recipe index is populated for the meal planner.
  // It is otherwise only loaded by the Recipes tab, so a user who goes straight
  // to "Planifier ma semaine" would generate against an empty index and get
  // blank cards. Only loads when empty (no loop, never overwrites a fresh index).
  useEffect(() => {
    if (recipeIndexEntries.length > 0) return;
    if (!isFirebaseEnabled()) return;
    let cancelled = false;
    (async () => {
      try {
        const entries = await unifiedRecipeService.getAllRecipes(uid ?? undefined);
        if (!cancelled && entries.length > 0) {
          dispatch(setRecipeIndex({ entries, userId: uid }));
        }
      } catch (err) {
        console.warn('Failed to preload recipe index for meal planner:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recipeIndexEntries.length, uid, dispatch]);

  /*
   * There used to be an effect here that generated seven dinners 800ms after
   * landing on any empty viewed week, guarded by a single `useRef` holding one
   * week key. `mealPlanSyncMiddleware` persists what it produced, so browsing
   * forward silently created and uploaded plans nobody asked for.
   *
   * It was doing three jobs, and only the first was the hazard: it filled a
   * week the cook had browsed to, it refilled the week after "Vider le plan",
   * and it was the phone's entire empty state — `thisWeek.generateMyWeek` was
   * wired only into the two desktop layouts. All three are now one deliberate
   * tap: `WeekEmptyState` on the phone, the existing button on desktop, and
   * `ExtendPlanCard` at the end of the list for the days after the plan (#722).
   */

  // Clear week plan
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleClearPlan = useCallback(() => {
    setShowClearConfirm(true);
  }, []);

  /**
   * Everything the generator is promised, shared by the full-week generator and
   * "the next 7 days" (#722) so the two can never disagree about allergies,
   * diets or taste.
   */
  const loadGenerationInputs = useCallback(async (previouslyGeneratedRecipeIds: string[]) => {
    // The catalogue and the cook's own recipes, as Régénérer and « Remplacer »
    // read them — not only the bundled 481.
    const allRecipes = await loadPlannerLibrary({ language: currentLanguage, uid, indexed: recipeIndexEntries });

    let deals: typeof allDeals = [];
    if (lufaEnabled) {
      try {
        const result = await dispatch(fetchLufaDeals()).unwrap();
        deals = result.deals;
      } catch (e) {
        console.warn('Could not fetch Lufa deals, generating without:', e);
        deals = allDeals;
      }
    }

    const config: DinnerMVPConfig = {
      excludedRecipeIds: [],
      preferredServings: 4,
      inventory: activeInventory,
      mealSchedule,
      deals: deals.length > 0 ? deals : undefined,
      previouslyGeneratedRecipeIds: previouslyGeneratedRecipeIds.length > 0 ? previouslyGeneratedRecipeIds : undefined,
      // Every promise the cook was made, on the same run: the hard rules
      // (allergies, excluded ingredients, declared diets) and both halves of
      // the taste reading — the axes they chose and the profile we learned.
      tasteProfile: tasteProfile || undefined,
      tasteAxes: tasteAxes || undefined,
      allergenTerms: buildAllergenTerms(allergens, customAllergens),
      dislikedIngredients: dislikedIngredients.length > 0 ? [...dislikedIngredients] : undefined,
      dietaryRestrictions: dietaryRestrictions.length > 0 ? [...dietaryRestrictions] as DinnerMVPConfig['dietaryRestrictions'] : undefined,
    };
    return { allRecipes, config };
  }, [uid, recipeIndexEntries, lufaEnabled, dispatch, allDeals, activeInventory, mealSchedule, tasteProfile, tasteAxes, allergens, customAllergens, dislikedIngredients, dietaryRestrictions, currentLanguage]);

  /** A generated dinner, in the shape the plan stores. */
  const toPlannedMeal = useCallback((dinner: GeneratedDinner): PlannedMeal => ({
    id: `${dinner.slot.date}-dinner-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    recipeId: dinner.recipe.id,
    recipe: {
      id: dinner.recipe.id,
      title: dinner.recipe.title,
      image: dinner.recipe.image || '',
      readyInMinutes: dinner.recipe.readyInMinutes,
      servings: defaultFamilySize,
      healthScore: dinner.recipe.healthScore,
      dishTypes: dinner.recipe.dishTypes,
      cuisines: dinner.recipe.cuisines,
      diets: dinner.recipe.diets,
      originalLanguage: dinner.recipe.originalLanguage,
      sourceUrl: dinner.recipe.sourceUrl,
      sourceRating: dinner.recipe.sourceRating,
      sourceRatingCount: dinner.recipe.sourceRatingCount,
      extendedIngredients: dinner.recipe.ingredients.map((name, idx) => {
        const parsed = parseIngredientText(name);
        return {
          id: idx,
          name: parsed.remainder || name,
          original: name,
          amount: parseAmount(name) ?? 0,
          unit: parsed.unitPart || '',
        };
      }),
      instructions: [],
      summary: '',
    },
    scheduledFor: dinner.slot.date,
    dayOfWeek: dinner.slot.dayOfWeek,
    mealType: 'dinner' as MealType,
    servings: defaultFamilySize,
    createdAt: new Date().toISOString(),
    ingredientsReviewed: false,
    missingIngredients: [],
    matchedDeals: dinner.matchedDeals,
    matchMetadata: dinner.matchMetadata ? {
      inventoryMatchPercent: dinner.matchMetadata.inventoryMatchPercent,
      usesExpiringIngredients: dinner.matchMetadata.usesExpiringIngredients,
      isNewRecipe: dinner.matchMetadata.isNewRecipe,
      constraintLabel: dinner.matchMetadata.constraintLabel,
      tasteProfileBonus: dinner.matchMetadata.tasteProfileBonus,
      excitementScore: dinner.matchMetadata.excitementScore,
      dealCount: dinner.matchMetadata.dealCount,
    } : undefined,
  }), [defaultFamilySize]);

  // One-tap MVP generation - generates 7 dinners immediately
  /**
   * `targetWeek` lets a caller generate a week it is navigating to. Reading
   * `currentWeekStart` instead would generate the week being left behind:
   * `setCurrentWeek` and this call happen in the same tick, and this callback
   * closed over the old value.
   *
   * Only a string is trusted. This function is also handed straight to
   * `onPress`, which calls it with the press event — and an event object walks
   * through `addDays` without throwing, producing meals filed under weeks that
   * do not exist. It generated seven dinners and drew none of them, silently.
   */
  const handleGenerateMVP = useCallback(async (targetWeek?: string) => {
    const weekStartDate = typeof targetWeek === 'string' ? targetWeek : currentWeekStart;
    const weekMealsNow = selectMealsForWeek(weekStartDate)(store.getState());
    setIsGenerating(true);

    try {
      // Minimum delay for UX (show "Creating magic..." for at least 2 seconds)
      const minDelayPromise = new Promise(resolve => setTimeout(resolve, 2000));

      // Collect current plan's recipe IDs for regeneration exclusion (exclude entry sentinel ids)
      const previouslyGeneratedRecipeIds = weekMealsNow
        .filter(m => isRecipeMeal(m))
        .map(m => m.recipeId);
      console.log(`🔄 Regeneration exclusion: ${previouslyGeneratedRecipeIds.length} recipes from current plan`);

      const { allRecipes, config } = await loadGenerationInputs(previouslyGeneratedRecipeIds);

      if (allRecipes.length === 0) {
        showToast(t('thisWeek.noRecipesAvailable'), 'error');
        return;
      }

      /**
       * Seven days from today, not "today to Sunday". Days already gone are
       * still skipped, but the horizon now runs past Sunday: a Friday
       * regeneration used to produce three soupers, and 41% of all generated
       * plans came in short the same way.
       *
       * It spills only onto an empty next week. `setWeeklyPlan` replaces a
       * bucket, so spilling onto a week the cook has already planned would
       * silently erase it — and a week that already has food is not the
       * problem this solves.
       */
      const nextWeekMeals = selectMealsForWeek(addDays(weekStartDate, 7))(store.getState());
      const range = horizonRange(weekStartDate, todayIso(), nextWeekMeals.length === 0);
      // What this plan must not repeat (#736): two weeks either side of the
      // days being rewritten, minus those days themselves — they are replaced.
      const rewrittenUntil = range.spills ? range.endsAt : addDays(weekStartDate, 6);
      const sameMealHistory = historyFromMeals(
        selectMealsForDateRange(
          store.getState(),
          addDays(range.startsAt, -SAME_MEAL_LOOKBACK_DAYS),
          addDays(rewrittenUntil, SAME_MEAL_LOOKBACK_DAYS),
        ).filter(m => isRecipeMeal(m) && (m.scheduledFor < range.startsAt || m.scheduledFor > rewrittenUntil)),
      );
      const generatedDinners = generateHorizonDinners(
        weekStartDate,
        allRecipes,
        { ...config, sameMealHistory },
        allMetadata,
        range,
      );

      // Build generation summary for transparency banner
      const summary = buildGenerationSummary(generatedDinners);
      setGenerationSummary(summary);

      const plannedMeals: PlannedMeal[] = generatedDinners.map(toPlannedMeal);

      // Debug: check matchedDeals after conversion
      const mealsWithDeals = plannedMeals.filter(m => m.matchedDeals && m.matchedDeals.length > 0);
      console.log('🥬 PlannedMeals with matchedDeals:', mealsWithDeals.length, '/', plannedMeals.length);

      // Wait for minimum delay before showing results (UX enhancement)
      await minDelayPromise;

      // Emit silent_regen event if replacing an existing plan
      const oldMealCount = weekMealsNow.length;
      if (oldMealCount > 0) {
        dispatch(addFeedbackEvent(createSilentRegenEvent(
          weekStartDate,
          oldMealCount,
          plannedMeals.length,
        )));
      }

      // One dispatch per week bucket: mealPlanSyncMiddleware syncs the single
      // week named in the payload, so a spilled plan written in one go would
      // live in Redux and never reach Firestore.
      //
      // `source` rides along so `week_plan_generated` can tell a plan the cook
      // asked for from one written by onboarding. Every bucket of one
      // generation carries the same value; the analytics middleware coalesces
      // them into a single event.
      for (const [weekStartDate, meals] of groupMealsByWeek(plannedMeals)) {
        dispatch(setWeeklyPlan({ weekStartDate, meals, source: 'user' }));
      }

      console.log(`Generated ${plannedMeals.length} MVP dinners for week starting ${weekStartDate}`);

      // Rehydrate recipe ingredients with full details (async, non-blocking)
      const recipeIds = plannedMeals.map(m => m.recipeId);
      rehydrateRecipeIngredients(recipeIds);

      // Translate recipe titles to current app language (async, non-blocking)
      translateGeneratedMeals(plannedMeals);

    } catch (error) {
      console.error('Error generating MVP plan:', error);
      showToast(t('thisWeek.generateFailed'), 'error');
    } finally {
      setIsGenerating(false);
    }
  }, [currentWeekStart, store, allMetadata, loadGenerationInputs, toPlannedMeal, dispatch, showToast, rehydrateRecipeIngredients, translateGeneratedMeals]);

  /**
   * "Planifier les 7 jours suivants" (#722): seven dinners from `startDate`,
   * handed to `commit`, which writes them fill-only and owns the undo.
   *
   * It never writes itself. The phone's one-scroll list takes the undo
   * snapshot and does the merge against the store as it is at that instant,
   * so a week the cook already planned is added to, never replaced — see
   * `mergeFillOnly`. Returns the dinners that actually went in.
   */
  const handleExtendPlan = useCallback(async (
    startDate: string,
    commit: (meals: PlannedMeal[]) => PlannedMeal[],
  ): Promise<PlannedMeal[]> => {
    if (typeof startDate !== 'string') return [];
    const weekStartDate = weekStartOf(startDate);
    const endsAt = addDays(startDate, HORIZON_DAYS - 1);
    const nextWeekStart = addDays(weekStartDate, 7);
    const range = { startsAt: startDate, endsAt, nextWeekStart, spills: endsAt >= nextWeekStart };

    try {
      // Don't repeat the week just before, nor anything already in the window.
      const around = selectMealsForDateRange(store.getState(), addDays(startDate, -7), endsAt);
      const recent = around.filter(m => isRecipeMeal(m)).map(m => m.recipeId);
      const { allRecipes, config } = await loadGenerationInputs(recent);
      if (allRecipes.length === 0) {
        showToast(t('thisWeek.noRecipesAvailable'), 'error');
        return [];
      }
      // Fill-only: everything already planned stays, so all of it counts (#736).
      const sameMealHistory = historyFromMeals(
        selectMealsForDateRange(
          store.getState(),
          addDays(startDate, -SAME_MEAL_LOOKBACK_DAYS),
          addDays(endsAt, SAME_MEAL_LOOKBACK_DAYS),
        ).filter(m => isRecipeMeal(m)),
      );

      const dinners = generateHorizonDinners(weekStartDate, allRecipes, { ...config, sameMealHistory }, allMetadata, range)
        .filter(d => d.slot.date >= startDate && d.slot.date <= endsAt);
      const added = commit(dinners.map(toPlannedMeal));

      if (added.length > 0) {
        rehydrateRecipeIngredients(added.map(m => m.recipeId));
        translateGeneratedMeals(added);
      }
      return added;
    } catch (error) {
      console.error('Error extending the plan:', error);
      showToast(t('thisWeek.generateFailed'), 'error');
      return [];
    }
  }, [store, allMetadata, loadGenerationInputs, toPlannedMeal, showToast, t, rehydrateRecipeIngredients, translateGeneratedMeals]);

  // Generate week plan with wizard config
  const handleGenerateWeek = useCallback(async (config: GenerateConfig) => {
    setIsGenerating(true);

    try {
      // Load all personal recipes
      const allRecipes = await recipeService.getAllPersonalRecipes(currentLanguage);

      if (allRecipes.length === 0) {
        showToast(t('thisWeek.noRecipesAvailable'), 'error');
        setIsGenerating(false);
        setShowGenerateModal(false);
        return;
      }

      // Get previous week's recipe IDs
      const previousWeekStart = new Date(currentWeekStart + 'T12:00:00');
      previousWeekStart.setDate(previousWeekStart.getDate() - 7);

      // This would come from Redux but we'll use empty for now
      const previousWeekRecipeIds: string[] = [];

      // Build dietary config from wizard preferences
      const dietaryConfig = {
        includeVegetarian: config.dietaryPreferences.includeVegetarian,
        includeFish: config.dietaryPreferences.includeFish,
      };

      // Generate the plan for ALL slots first (with dietary preferences and deals)
      const generatedMeals = weekGeneratorService.generateWeeklyPlan(
        currentWeekStart,
        allRecipes,
        previousWeekRecipeIds,
        allMetadata,
        {
          dietaryConfig,
          selectedDeals: selectedDeals.length > 0 ? selectedDeals : undefined,
        }
      );

      // Create a set of selected slot keys for filtering
      const selectedSlotKeys = new Set(
        config.selectedSlots.map(slot => `${slot.date}-${slot.mealType}`)
      );

      // Filter generated meals to only include selected slots
      const filteredMeals = generatedMeals.filter(gm => {
        const slotKey = `${gm.slot.date}-${gm.slot.mealType}`;
        return selectedSlotKeys.has(slotKey);
      });

      // Convert to PlannedMeal format with selected servings
      const plannedMeals: PlannedMeal[] = filteredMeals.map(gm => ({
        id: `${gm.slot.date}-${gm.slot.mealType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        recipeId: gm.recipe.id,
        recipe: {
          id: gm.recipe.id,
          title: gm.recipe.title,
          image: gm.recipe.image || '',
          readyInMinutes: gm.recipe.readyInMinutes,
          servings: config.servings, // Use selected servings
          healthScore: gm.recipe.healthScore,
          dishTypes: gm.recipe.dishTypes,
          cuisines: gm.recipe.cuisines,
          diets: gm.recipe.diets,
          originalLanguage: gm.recipe.originalLanguage,
          sourceUrl: gm.recipe.sourceUrl,
          sourceRating: gm.recipe.sourceRating,
          sourceRatingCount: gm.recipe.sourceRatingCount,
          extendedIngredients: gm.recipe.ingredients.map((name, idx) => {
            const parsed = parseIngredientText(name);
            return {
              id: idx,
              name: parsed.remainder || name,
              original: name,
              amount: parseAmount(name) ?? 0,
              unit: parsed.unitPart || '',
            };
          }),
          instructions: [],
          summary: '',
        },
        scheduledFor: gm.slot.date,
        dayOfWeek: gm.slot.dayOfWeek,
        mealType: gm.slot.mealType,
        servings: config.servings, // Use selected servings
        createdAt: new Date().toISOString(),
        ingredientsReviewed: false,
        missingIngredients: [],
        dietaryRole: gm.dietaryRole, // Preserve dietary role for "Suggest Another"
        matchedDeals: gm.matchedDeals, // Deals that match this meal's ingredients
      }));

      // Dispatch to Redux
      dispatch(setWeeklyPlan({ weekStartDate: currentWeekStart, meals: plannedMeals, source: 'wizard' }));

      // Log config for future dietary filtering implementation
      console.log(`Generated ${plannedMeals.length} meals for week starting ${currentWeekStart}`);
      console.log(`Servings: ${config.servings}, Dietary: veg=${config.dietaryPreferences.includeVegetarian}, fish=${config.dietaryPreferences.includeFish}`);

      // Rehydrate recipe ingredients with full details (async, non-blocking)
      const recipeIds = plannedMeals.map(m => m.recipeId);
      rehydrateRecipeIngredients(recipeIds);

      // Translate recipe titles to current app language (async, non-blocking)
      translateGeneratedMeals(plannedMeals);

      setShowGenerateModal(false);
    } catch (error) {
      console.error('Error generating week plan:', error);
      showToast(t('thisWeek.generateFailed'), 'error');
    } finally {
      setIsGenerating(false);
    }
  }, [currentWeekStart, allMetadata, dispatch, showToast, rehydrateRecipeIngredients, translateGeneratedMeals, currentLanguage]);

  // Handle meal card press - navigate to recipe detail (skip free-text meals)
  const handleMealPress = useCallback((meal: PlannedMeal) => {
    if (meal.recipeId === 'free-text') return;
    router.push({
      pathname: `/(app)/recipe-detail/${meal.recipeId}`,
      params: {
        plannedServings: String(meal.servings),
        slotDate: meal.scheduledFor,
        slotMealType: meal.mealType,
        ...(meal.recipe ? { recipeData: JSON.stringify(meal.recipe) } : {}),
      },
    });
  }, [router]);

  // Close ingredient review sheet
  const handleCloseReview = useCallback(() => {
    setIsReviewSheetVisible(false);
    setReviewMeal(null);
  }, []);

  // Handle swap button tap — open preview sheet
  const handleSuggestAnother = useCallback((meal: PlannedMeal) => {
    setSwapSheetMeal(meal);
    setSwapSheetVisible(true);
  }, []);

  // Commit a swap chosen in SwapSuggestionSheet
  const handleConfirmSwap = useCallback(async (meal: PlannedMeal, suggestion: PersonalRecipeIndexEntry) => {
    dispatch(replaceMeal({
      mealId: meal.id,
      newRecipe: {
        id: suggestion.id,
        title: suggestion.title,
        image: suggestion.image || '',
        readyInMinutes: suggestion.readyInMinutes,
        servings: parseInt(suggestion.servings) || 4,
        healthScore: suggestion.healthScore,
        dishTypes: suggestion.dishTypes,
        cuisines: suggestion.cuisines,
        diets: suggestion.diets,
        originalLanguage: suggestion.originalLanguage,
        extendedIngredients: suggestion.ingredients.map((name, idx) => {
          const parsed = parseIngredientText(name);
          return {
            id: idx,
            name: parsed.remainder || name,
            original: name,
            amount: parseAmount(name) ?? 0,
            unit: parsed.unitPart || '',
          };
        }),
        instructions: [],
        summary: '',
      },
    }));

    rehydrateRecipeIngredients([suggestion.id]);

    if (suggestion.originalLanguage && suggestion.originalLanguage !== 'unknown' && suggestion.originalLanguage !== currentLanguage) {
      translateGeneratedMeals([{
        recipeId: suggestion.id,
        recipe: { title: suggestion.title, originalLanguage: suggestion.originalLanguage } as Recipe,
      } as PlannedMeal]);
    }

  }, [dispatch, rehydrateRecipeIngredients, currentLanguage, translateGeneratedMeals]);

  // Handle swipe left - show feedback modal then remove
  const handleDeleteMeal = useCallback((meal: PlannedMeal) => {
    setPendingFeedbackMeal(meal);
    setFeedbackContext('remove');
    setFeedbackModalVisible(true);
  }, []);

  // Actually execute the remove action
  const executeRemoveMeal = useCallback((meal: PlannedMeal) => {
    dispatch(removeFromWeek(meal.id));
  }, [dispatch]);

  // Actually execute the replace action
  const executeReplaceMeal = useCallback(async (meal: PlannedMeal) => {
    try {
      const allRecipes = await recipeService.getAllPersonalRecipes(currentLanguage);
      const currentRecipeIds = currentWeekMeals.map(m => m.recipeId);

      const slot: WeekSlot = {
        date: meal.scheduledFor,
        dayOfWeek: meal.dayOfWeek,
        dayName: '',
        mealType: meal.mealType,
        isWeekend: meal.dayOfWeek === 0 || meal.dayOfWeek === 6,
      };

      const suggestion = weekGeneratorService.getSuggestion(
        slot,
        allRecipes,
        [meal.recipeId],
        currentRecipeIds,
        [],
        allMetadata,
        meal.dietaryRole,
      );

      if (suggestion) {
        dispatch(replaceMeal({
          mealId: meal.id,
          newRecipe: {
            id: suggestion.id,
            title: suggestion.title,
            image: suggestion.image || '',
            readyInMinutes: suggestion.readyInMinutes,
            servings: parseInt(suggestion.servings) || 4,
            healthScore: suggestion.healthScore,
            dishTypes: suggestion.dishTypes,
            cuisines: suggestion.cuisines,
            diets: suggestion.diets,
            originalLanguage: suggestion.originalLanguage,
            sourceUrl: suggestion.sourceUrl,
            sourceRating: suggestion.sourceRating,
            sourceRatingCount: suggestion.sourceRatingCount,
            extendedIngredients: suggestion.ingredients.map((name, idx) => {
              const parsed = parseIngredientText(name);
              return {
                id: idx,
                name: parsed.remainder || name,
                original: name,
                amount: parseAmount(name) ?? 0,
                unit: parsed.unitPart || '',
              };
            }),
            instructions: [],
            summary: '',
          },
        }));

        rehydrateRecipeIngredients([suggestion.id]);

        // Translate the replacement recipe if needed
        if (suggestion.originalLanguage && suggestion.originalLanguage !== 'unknown' && suggestion.originalLanguage !== currentLanguage) {
          translateGeneratedMeals([{
            recipeId: suggestion.id,
            recipe: { title: suggestion.title, originalLanguage: suggestion.originalLanguage } as Recipe,
          } as PlannedMeal]);
        }
      } else {
        showToast(t('thisWeek.noAlternativeTryBrowse'), 'error');
      }
    } catch (error) {
      console.error('Error suggesting another meal:', error);
      showToast(t('thisWeek.noNewIdea'), 'error');
    }
  }, [currentWeekMeals, allMetadata, dispatch, rehydrateRecipeIngredients, currentLanguage, translateGeneratedMeals, showToast]);

  // Auto-trigger swap when arriving from email deep link
  useEffect(() => {
    if (!swapRecipeId || currentWeekMeals.length === 0) return;

    const mealToSwap = currentWeekMeals.find(m =>
      m.recipeId === swapRecipeId &&
      (swapDay === undefined || m.dayOfWeek === swapDay) &&
      (!swapMealType || m.mealType === swapMealType)
    );

    if (mealToSwap) {
      executeReplaceMeal(mealToSwap);
    }
  }, [swapRecipeId, currentWeekMeals.length]); // Only run once when meals load

  // Handle feedback submit — dispatch event then execute the pending action
  const handleFeedbackSubmit = useCallback((reason?: RejectionReason, freeText?: string) => {
    if (!pendingFeedbackMeal) return;

    const event: FeedbackEvent = {
      id: `fb-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'explicit',
      action: feedbackContext,
      recipeId: pendingFeedbackMeal.recipeId,
      recipeName: pendingFeedbackMeal.recipe?.title,
      date: new Date().toISOString(),
      weekStartDate: currentWeekStart,
      ...(reason ? { quickReason: reason } : {}),
      ...(freeText ? { detail: freeText } : {}),
    };
    dispatch(addFeedbackEvent(event));

    if (feedbackContext === 'remove') {
      executeRemoveMeal(pendingFeedbackMeal);
    } else {
      executeReplaceMeal(pendingFeedbackMeal);
    }

    setFeedbackModalVisible(false);
    setPendingFeedbackMeal(null);
  }, [pendingFeedbackMeal, feedbackContext, currentWeekStart, dispatch, executeRemoveMeal, executeReplaceMeal]);

  // Handle skip feedback — execute the pending action without creating event
  const handleFeedbackSkip = useCallback(() => {
    if (!pendingFeedbackMeal) return;

    if (feedbackContext === 'remove') {
      executeRemoveMeal(pendingFeedbackMeal);
    } else {
      executeReplaceMeal(pendingFeedbackMeal);
    }

    setFeedbackModalVisible(false);
    setPendingFeedbackMeal(null);
  }, [pendingFeedbackMeal, feedbackContext, executeRemoveMeal, executeReplaceMeal]);

  // Handle week navigation (prev/next arrows)
  const handleWeekChange = useCallback((direction: 'prev' | 'next') => {
    const newWeekStart = addDays(currentWeekStart, direction === 'next' ? 7 : -7);
    dispatch(setCurrentWeek(newWeekStart));
  }, [currentWeekStart, dispatch]);

  // Handle servings change
  const handleServingsChange = useCallback((mealId: string, newServings: number) => {
    const meal = currentWeekMeals.find(m => m.id === mealId);
    if (meal && meal.servings !== newServings) {
      dispatch(addFeedbackEvent(createServingsChangeEvent(
        currentWeekStart,
        meal.recipeId,
        meal.recipe?.title || '',
        meal.servings,
        newServings,
      )));
    }
    dispatch(updateMealServings({ mealId, servings: newServings }));
  }, [dispatch, currentWeekMeals, currentWeekStart]);

  // Handle drag-drop move meal
  const handleMoveMeal = useCallback((mealId: string, newDate: string, newMealType: MealType) => {
    // Check if destination slot is full before dispatching
    const destSlotCount = currentWeekMeals.filter(
      m => m.scheduledFor === newDate && m.mealType === newMealType && m.id !== mealId
    ).length;

    if (destSlotCount >= MAX_MEALS_PER_SLOT) {
      showToast(t('thisWeek.slotFullMax', { max: 3 }), 'error');
      return;
    }

    const meal = currentWeekMeals.find(m => m.id === mealId);
    if (meal) {
      dispatch(addFeedbackEvent(createSwapEvent(
        currentWeekStart,
        meal.recipeId,
        meal.recipe?.title || '',
        meal.scheduledFor,
        newDate,
        meal.mealType,
        newMealType,
      )));
    }
    dispatch(moveMeal({
      mealId,
      newScheduledFor: newDate,
      newMealType,
    }));
  }, [dispatch, currentWeekMeals, currentWeekStart, showToast]);

  // Handle mark done / undo done
  const handleMarkDone = useCallback((meal: PlannedMeal) => {
    if (meal.status === 'cooked') {
      dispatch(unmarkMealAsCooked({ mealId: meal.id }));
    } else {
      dispatch(markMealAsCooked({ mealId: meal.id }));
      const name = meal.recipe?.title || meal.freeText || t('thisWeek.meal');
      showToast(t('thisWeek.markedDone', { name }), 'success');
      // Emit feedback event for recipe meals
      if (meal.recipeId !== 'free-text' && meal.recipe) {
        dispatch(addFeedbackEvent(
          createMealCompletedEvent(currentWeekStart, meal.recipeId, meal.recipe.title, meal.servings)
        ));
      }
    }
  }, [dispatch, currentWeekStart, showToast, t]);

  // Handle day press - navigate to day detail
  const handleDayPress = useCallback((date: string) => {
    router.push(`/(app)/day-detail/${date}`);
  }, [router]);

  // Handle empty slot press - open suggestion sheet
  const handleEmptySlotPress = useCallback((date: string, mealType: MealType) => {
    // Parse the date to create a WeekSlot
    const dateObj = new Date(date + 'T12:00:00');
    const dayOfWeek = dateObj.getDay();

    const slot: WeekSlot = {
      date,
      dayOfWeek,
      dayName: formatWeekday(date, i18n.language),
      mealType,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    };

    setSuggestionSlot(slot);
    setShowSuggestionSheet(true);
  }, []);

  // Handle selecting a recipe from suggestion sheet
  const handleSelectSuggestion = useCallback((recipe: PersonalRecipeIndexEntry, slot: WeekSlot) => {
    dispatch(addToWeek({
      scheduledFor: slot.date,
      mealType: slot.mealType,
      servings: defaultFamilySize,
      recipe: {
        id: recipe.id,
        title: recipe.title,
        image: recipe.image || '',
        readyInMinutes: recipe.readyInMinutes,
        servings: parseInt(recipe.servings) || 4,
        healthScore: recipe.healthScore,
        dishTypes: recipe.dishTypes,
        cuisines: recipe.cuisines,
        diets: recipe.diets,
        sourceUrl: recipe.sourceUrl,
        sourceRating: recipe.sourceRating,
        sourceRatingCount: recipe.sourceRatingCount,
        extendedIngredients: recipe.ingredients.map((name, idx) => {
          const parsed = parseIngredientText(name);
          return {
            id: idx,
            name: parsed.remainder || name,
            original: name,
            amount: parseAmount(name) ?? 0,
            unit: parsed.unitPart || '',
          };
        }),
        instructions: [],
        summary: '',
      },
    }));

    // Rehydrate with full ingredient data
    rehydrateRecipeIngredients([recipe.id]);

    setShowSuggestionSheet(false);
    setSuggestionSlot(null);
  }, [dispatch, defaultFamilySize, rehydrateRecipeIngredients]);

  // Handle browse all from suggestion sheet
  const handleBrowseAll = useCallback(() => {
    const slotDate = suggestionSlot?.date;
    const slotMealType = suggestionSlot?.mealType;
    setShowSuggestionSheet(false);
    setSuggestionSlot(null);
    router.push({
      pathname: '/(app)/recipes',
      params: {
        ...(slotDate && { slotDate }),
        ...(slotMealType && { slotMealType }),
      },
    });
  }, [router, suggestionSlot]);

  // Handle adding a free-text meal — open the custom meal modal
  const handleAddFreeTextMeal = useCallback((slot: WeekSlot) => {
    setShowSuggestionSheet(false);
    setSuggestionSlot(null);
    setCustomMealSlot(slot);
    setShowCustomMealModal(true);
  }, []);

  // Handle custom meal modal submission
  const handleCustomMealSubmit = useCallback((text: string) => {
    if (customMealSlot) {
      // Check if replacing an existing generated meal at this slot
      const existingMeal = currentWeekMeals.find(
        m => m.scheduledFor === customMealSlot.date && m.mealType === customMealSlot.mealType
      );
      if (existingMeal && existingMeal.recipeId !== 'free-text') {
        dispatch(addFeedbackEvent(createReplacedWithFreetextEvent(
          currentWeekStart,
          existingMeal.recipeId,
          existingMeal.recipe?.title || '',
          text,
        )));
      }

      dispatch(addFreeTextMeal({
        text,
        scheduledFor: customMealSlot.date,
        mealType: customMealSlot.mealType,
      }));
    }
    setShowCustomMealModal(false);
    setCustomMealSlot(null);
  }, [customMealSlot, dispatch, currentWeekMeals, currentWeekStart]);

  return (
    <DragDropProvider>
      <View style={styles.container}>

        {/* Generation Summary Banner — only for the mid-size WeekDesktopView (800–1180px).
            The desktop revamp (≥1180px) and the mobile revamp (≤800px / native) own their own thinking UI. */}
        {generationSummary && isDesktopWeb() && windowWidth > 800 && windowWidth < 1180 && <GenerationSummaryBanner summary={generationSummary} />}

        {/* Week View — v2 when idle, legacy mid-size desktop as fallback for 800–1180px.
            A touch tablet in landscape joins the v2 grid here and never reaches the
            legacy branch below: that one's drag arms from a mouseenter handler gated
            on isDesktopWeb(), so it is dead on touch (#646). */}
        {configPhase === 'idle' && (plannerLayout === 'grid' || (isDesktopWeb() && windowWidth >= 1180)) ? (
          <WeeklyDesktop />
        ) : configPhase === 'idle' && isDesktopWeb() && windowWidth > 800 ? (
          <WeekDesktopView
            weekStartDate={currentWeekStart}
            meals={currentWeekMeals}
            onMealPress={handleMealPress}
            onEmptySlotPress={handleEmptySlotPress}
            onDeleteMeal={handleDeleteMeal}
            onSuggestAnother={handleSuggestAnother}
            onWeekChange={handleWeekChange}
            onMoveMeal={handleMoveMeal}
            onServingsChange={handleServingsChange}
            onClearPlan={handleClearPlan}
            hasAnyMeals={hasAnyMeals}
            onDayPress={handleDayPress}
            onMarkDone={handleMarkDone}
            onGenerateWeek={handleGenerateMVP}
            isGenerating={isGenerating}
          />
        ) : configPhase === 'idle' ? (
          <WeeklyMobile onGenerateWeek={handleGenerateMVP} onExtendPlan={handleExtendPlan} isGenerating={isGenerating} />
        ) : isDesktopWeb() && windowWidth >= 1180 ? (
          <MealConfigDesktop />
        ) : isDesktopWeb() && windowWidth > 800 ? (
          <WeekDesktopView
            weekStartDate={currentWeekStart}
            meals={currentWeekMeals}
            onMealPress={handleMealPress}
            onEmptySlotPress={handleEmptySlotPress}
            onDeleteMeal={handleDeleteMeal}
            onSuggestAnother={handleSuggestAnother}
            onWeekChange={handleWeekChange}
            onMoveMeal={handleMoveMeal}
            onServingsChange={handleServingsChange}
            onClearPlan={handleClearPlan}
            hasAnyMeals={hasAnyMeals}
            onDayPress={handleDayPress}
            onMarkDone={handleMarkDone}
            onGenerateWeek={handleGenerateMVP}
            isGenerating={isGenerating}
          />
        ) : (
          <MealConfigMobile />
        )}

      {/* Generate Wizard */}
      <GenerateWizard
        visible={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleGenerateWeek}
        isGenerating={isGenerating}
        weekStartDate={currentWeekStart}
      />

      {/* Ingredient Review Sheet */}
      <IngredientReviewSheet
        visible={isReviewSheetVisible}
        meal={reviewMeal}
        onClose={handleCloseReview}
      />

      {/* Recipe Suggestion Sheet */}
      <RecipeSuggestionSheet
        visible={showSuggestionSheet}
        slot={suggestionSlot}
        emptySlots={emptySlots}
        allRecipes={allRecipesLoaded}
        currentWeekRecipeIds={currentWeekMeals.map(m => m.recipeId)}
        previousWeekRecipeIds={[]}
        metadata={allMetadata}
        dietaryConfig={dietaryConfig}
        onSelectRecipe={handleSelectSuggestion}
        onBrowseAll={handleBrowseAll}
        onAddFreeText={handleAddFreeTextMeal}
        onClose={() => {
          setShowSuggestionSheet(false);
          setSuggestionSlot(null);
        }}
      />

      {/* Swap Suggestion Sheet */}
      <SwapSuggestionSheet
        visible={swapSheetVisible}
        meal={swapSheetMeal}
        allRecipes={allRecipesLoaded}
        currentWeekRecipeIds={currentWeekMeals.map(m => m.recipeId)}
        metadata={allMetadata}
        onConfirm={handleConfirmSwap}
        onClose={() => {
          setSwapSheetVisible(false);
          setSwapSheetMeal(null);
        }}
      />

      {/* Custom Meal Modal */}
      <AddCustomMealModal
        visible={showCustomMealModal}
        slotLabel={customMealSlot ? `${customMealSlot.dayName} · ${String(t(`thisWeek.mealTypes.${customMealSlot.mealType}`)).toLowerCase()}` : undefined}
        onClose={() => {
          setShowCustomMealModal(false);
          setCustomMealSlot(null);
        }}
        onAdd={handleCustomMealSubmit}
      />

      {/* Clear Confirmation Modal */}
      <ConfirmationModal
        visible={showClearConfirm}
        title={t('thisWeek.clearPlanTitle')}
        message={t('thisWeek.clearPlanMessage')}
        confirmLabel={t('thisWeek.clearPlanConfirm')}
        destructive
        onConfirm={() => {
          dispatch(clearWeek(currentWeekStart));
          setShowClearConfirm(false);
        }}
        onCancel={() => setShowClearConfirm(false)}
      />

      {/* Feedback Modal */}
      <SkipFeedbackModal
        visible={feedbackModalVisible}
        recipeName={pendingFeedbackMeal?.recipe?.title || ''}
        context={feedbackContext}
        onSubmit={handleFeedbackSubmit}
        onSkipFeedback={handleFeedbackSkip}
      />

      {/* Fridge snap nudge — bottom sheet on mobile, centered dialog on desktop */}

      {/* Toast */}
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onDismiss={() => setToastVisible(false)}
      />

      {/* Loading Overlay — only for the mid-size WeekDesktopView (800–1180px); both the desktop
          revamp (≥1180px) and the mobile revamp (≤800px / native) own their own thinking UI. */}
      {isGenerating && hasAnyMeals && isDesktopWeb() && windowWidth > 800 && windowWidth < 1180 && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={Paprika.accent} />
          <Text style={styles.loadingText}>{t('thisWeek.creatingYourPlan')}</Text>
          <Text style={styles.loadingSubtext}>{t('thisWeek.planningYourWeek')}</Text>
        </View>
      )}
      </View>
    </DragDropProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },

  // Generate Button
  generateButtonContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: PaprikaSpacing.lg,
    backgroundColor: Paprika.background,
    borderTopWidth: 1,
    borderTopColor: Paprika.borderLight,
  },
  generateButton: {
    backgroundColor: Paprika.accent,
    paddingVertical: 16,
    borderRadius: PaprikaRadius.xl,
    alignItems: 'center',
  },
  generateButtonDisabled: {
    opacity: 0.7,
  },
  generateButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.background,
  },

  // Loading Overlay
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  loadingText: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginTop: PaprikaSpacing.lg,
  },
  loadingSubtext: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginTop: PaprikaSpacing.xs,
  },

  // Fridge snap nudge
});

export default TheWeeklyScreen;
